import { FieldArray } from 'react-final-form-arrays';
import { useCallback } from 'react';
import { FormSpy } from 'react-final-form';

import { lodashGet } from 'functions/lodashGet';
import { Button } from 'UIPackage/MainModule/components/Button';
import {
  IKnmErknmFormViolation,
  IKnmErknmFormViolationFix,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ErknmDecisionViolationFixItem } from 'DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixItem';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';

import styles from './ErknmDecisionViolationFixItems.module.scss';

interface IErknmDecisionViolationFixItemsProps {
  violation: IKnmErknmFormViolation;
  name: string;
  isFixedFieldName: string;
  id: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  watchOnly: boolean;
  isPaperless: boolean;
  inAct?: boolean;
  isDisabledByDirty?: boolean;
  isDisabledByAgreement?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  isFixed?: boolean;
  inAgreement?: boolean;
  isSupervisor?: boolean;
  watchOnlyRange?: boolean;
  isExpandAll?: boolean;
  hasUnsignedAct?: boolean;
  isRepeatCheck?: boolean;
}

export const ErknmDecisionViolationFixItems = ({
  name,
  isFixedFieldName,
  formRef,
  violation,
  watchOnly,
  isPaperless,
  inAct,
  isDisabledByDirty = false,
  isDisabledByAgreement = false,
  hasUnsignedAct = false,
  isKnm,
  isPm,
  isDecision,
  id,
  isFixed,
  isSupervisor,
  inAgreement,
  watchOnlyRange,
  isExpandAll,
  isRepeatCheck,
}: IErknmDecisionViolationFixItemsProps) => {
  const fixesFieldName = `${name}.fixes`;
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  // Добавлять новый материал по устранению нельзя (isAddFixBlocked === true), если:
  // - последний материал ещё черновой (не сохранён), либо
  // - нарушение уже устранено (RESOLVED)
  // Статус WAITING_CONFIRMATION (APPROVING) здесь намеренно НЕ блокирует: для него
  // добавление материалов разрешено (см. разрешающее условие canFixBeAdded ниже).
  const isAddFixBlocked =
    violation.fixes?.[violation.fixes?.length - 1]?.isNew || violation.status === ViolationStatusesEnum.FIXED;

  const isBlockFixAddByWatchOnly = isRepeatCheck || !watchOnly;

  const canFixBeAdded: boolean =
    // если статус нарушения NOT_RESOLVED || статус нарушения WAITING_CONFIRMATION
    // Можно редактировать нарушения если еще акт не был подписан
    (hasUnsignedAct ||
      violation.status === ViolationStatusesEnum.NOT_FIXED ||
      violation.status === ViolationStatusesEnum.APPROVING ||
      (violation.status === ViolationStatusesEnum.SUSPENDED_BY_AGREEMENT && !!isSupervisor)) &&
    isBlockFixAddByWatchOnly &&
    (!isAddFixBlocked || hasUnsignedAct);

  const handleAddFix = useCallback(() => {
    const newFix: Partial<IKnmErknmFormViolationFix> = {
      isNew: true,
    };

    push && push(fixesFieldName, newFix);
  }, []);
  const getRemoveViolationFixHandler = useCallback(
    index => () => {
      remove && remove(fixesFieldName, index);
    },
    [],
  );

  const fixesValidation = useCallback(
    (value: IKnmErknmFormViolationFix[]) => {
      if (isFixed && canFixBeAdded && (!value || value.length === 0)) return REQUIRED_ERROR;
    },
    [isFixed, canFixBeAdded],
  );

  return (
    <>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Сведения о факте устранения нарушения',
          field: fixesFieldName,
          isPm,
          isKnm,
          isDecision,
        })}
        margin="top"
        id={`${id}.fixes`}
        button={
          canFixBeAdded ? (
            <Button
              className="shared-btn-add"
              text="Добавить материалы"
              type="button"
              variant="link"
              color="primary"
              onClick={handleAddFix}
              disabled={isDisabledByDirty || isDisabledByAgreement}
            />
          ) : undefined
        }
      />
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let fieldError: any;

          try {
            fieldError = lodashGet(props.errors || {}, fixesFieldName);
          } catch (e) {
            fieldError = undefined;
          }
          const isTouched = props.touched?.[fixesFieldName];

          if (isTouched && fieldError && fieldError['FINAL_FORM/array-error']) {
            return (
              <p className={`shared-input-error shared-row__small-left-margin ${styles.ErrorMsgWrapper}`}>
                {fieldError['FINAL_FORM/array-error']}
              </p>
            );
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={fixesFieldName} validate={fixesValidation}>
        {({ fields }) =>
          fields.map((fixFieldNameName, index) => {
            const fixValue: IKnmErknmFormViolationFix = fields.value[index];

            return (
              <ErknmDecisionViolationFixItem
                violation={violation}
                fix={fixValue}
                fixIdx={index}
                fieldName={fixFieldNameName}
                name={name}
                isFixedFieldName={isFixedFieldName}
                formRef={formRef}
                key={fixFieldNameName}
                watchOnly={watchOnly}
                isDisabledByDirty={isDisabledByDirty}
                isDisabledByAgreement={isDisabledByAgreement}
                hasUnsignedAct={hasUnsignedAct}
                onRemove={getRemoveViolationFixHandler(index)}
                isPaperless={isPaperless}
                inAct={inAct}
                inAgreement={inAgreement}
                isKnm={isKnm}
                isPm={isPm}
                isDecision={isDecision}
                id={`${id}.fixes[${index}]`}
                watchOnlyRange={watchOnlyRange}
                isExpandAll={isExpandAll}
              />
            );
          })
        }
      </FieldArray>
    </>
  );
};
